/**
 * Configuration des pages SEO locales indépendantes.
 *
 * Chaque entrée définit une URL racine du type `/nettoyage-vitres-la-tremblade`
 * avec son contenu, sa FAQ, son JSON-LD et son maillage interne.
 *
 * Ajout d'une nouvelle page :
 *   1. Ajouter une entrée dans `LOCAL_PAGES` ci-dessous (clé = path sans le /).
 *   2. Aucune autre modification React nécessaire — App.js boucle sur ces entrées.
 *   3. Ajouter l'entrée correspondante dans `scripts/prerender.js` (LOCAL_PAGES_SEO)
 *      pour le pré-rendu SEO et le sitemap.
 *
 * Fields:
 *   - slug              path sans le `/` initial
 *   - city              ville ciblée (utilisée dans textes + breadcrumb)
 *   - service           libellé du service principal
 *   - title             <title> (60-65 caractères)
 *   - description       <meta description> (150-160 caractères)
 *   - h1                Titre principal H1 (peut différer du title)
 *   - intro             paragraphe d'intro sous le H1
 *   - heroImage         URL de l'image hero (CDN Emergent ou /img/)
 *   - heroImageAlt      alt SEO de l'image
 *   - priceRange        ex: "60-180 €"
 *   - delays            ex: "48-72 h"
 *   - whyUs             tableau de { title, desc } — section "Pourquoi nous"
 *   - process           tableau d'étapes { title, desc }
 *   - zones             villes desservies depuis cette page (maillage local)
 *   - faq               tableau de { q, a } — alimente FAQ + JSON-LD FAQPage
 *   - relatedLinks      maillage interne sortant (autres pages locales, blog, etc.)
 *   - serviceType       schema.org Service type (ex: "Window Cleaning")
 */

export const LOCAL_PAGES = {
  'nettoyage-vitres-la-tremblade': {
    slug: 'nettoyage-vitres-la-tremblade',
    city: 'La Tremblade',
    service: 'Nettoyage de vitres',
    serviceType: 'Window Cleaning',
    title: 'Nettoyage de vitres à La Tremblade — SMT Services (devis gratuit)',
    description:
      "Nettoyage de vitres professionnel à La Tremblade et Ronce-les-Bains : maisons, appartements, vérandas, baies vitrées. Devis gratuit sous 24h, crédit d'impôt 50 % particuliers. 4,8/5 sur Google.",
    h1: 'Nettoyage de vitres à La Tremblade',
    intro:
      "SMT Services est votre laveur de vitres professionnel à La Tremblade, Ronce-les-Bains et toute la presqu'île d'Arvert. Particuliers et professionnels : nous intervenons sur fenêtres, baies vitrées, vérandas et encadrements avec un matériel pro (perche télescopique à eau pure jusqu'à 14 m) pour un résultat sans traces. Devis gratuit sous 24h, intervention sous 48-72h, et 50 % de crédit d'impôt pour les particuliers.",
    heroImage:
      'https://customer-assets.emergentagent.com/job_77acf5b0-bce4-4f1b-9869-3607f8c62307/artifacts/f5yfb2nw_IMG_2618.jpeg',
    heroImageAlt:
      "Nettoyage de vitres professionnel à La Tremblade par SMT Services — résultat sans traces",
    priceRange: '60-180 €',
    delays: '48-72 h en basse saison, 5-8 jours en haute saison',
    whyUs: [
      {
        title: 'Spécialiste local',
        desc: "Basés à Chaillevette, nous connaissons les contraintes spécifiques de La Tremblade : air marin chargé en sel, embruns sur les baies vitrées côté Ronce-les-Bains, traces tenaces de salpêtre sur les vérandas.",
      },
      {
        title: 'Matériel professionnel',
        desc: "Perche télescopique en carbone à eau pure (osmosée + déionisée) jusqu'à 14 m de hauteur — résultat impeccable sans aucune trace, même sur les grandes baies vitrées.",
      },
      {
        title: "50 % de crédit d'impôt",
        desc: "Adhérents à la coopérative Unipros (services à la personne), nous vous faisons bénéficier automatiquement de 50 % de crédit d'impôt sur le nettoyage de vitres à votre domicile.",
      },
      {
        title: 'Devis transparent',
        desc: "Tarif clair annoncé à l'avance, pas de frais cachés, paiement uniquement après votre validation de l'intervention.",
      },
    ],
    process: [
      {
        title: '1. Demande de devis en ligne',
        desc: "Vous remplissez le formulaire en moins de 2 minutes. Réponse sous 24h avec un devis détaillé et le montant net après crédit d'impôt.",
      },
      {
        title: '2. Visite technique (si besoin)',
        desc: "Pour les maisons avec véranda ou grandes baies, nous passons sur place pour évaluer précisément le travail. Sans engagement.",
      },
      {
        title: '3. Intervention à La Tremblade',
        desc: "Notre équipe se déplace avec tout le matériel pro. Comptez 1h30 à 3h selon la taille de votre logement. Vitres, baies, encadrements, traces de calcaire — tout est traité.",
      },
      {
        title: '4. Paiement après satisfaction',
        desc: "Vous vérifiez le résultat avec nous. Paiement par CB, virement ou Cesu préfinancé. Facture remise sur place pour le crédit d'impôt.",
      },
    ],
    zones: [
      'La Tremblade centre',
      'Ronce-les-Bains',
      'La Grève',
      "L'Embellie",
      'Mus de Loup',
      "Plage de la Côte Sauvage",
    ],
    faq: [
      {
        q: 'Combien coûte un nettoyage de vitres à La Tremblade ?',
        a: "Nos tarifs 2026 à La Tremblade démarrent à 60 € pour un studio, 90-140 € pour un T3-T4, 120-180 € pour une maison plain-pied avec véranda. Pour les particuliers, déduisez 50 % de crédit d'impôt : un devis de 180 € vous revient à 90 € net. Les locations saisonnières et les commerces bénéficient de tarifs au m² ou au forfait selon la fréquence.",
      },
      {
        q: 'Intervenez-vous aussi à Ronce-les-Bains ?',
        a: "Oui, Ronce-les-Bains fait partie de la commune de La Tremblade et nous intervenons sans frais de déplacement supplémentaire. Nous nettoyons régulièrement les villas en bord de mer où l'air marin laisse des traces de sel particulièrement tenaces sur les baies vitrées et vérandas.",
      },
      {
        q: 'Sous quel délai pouvez-vous intervenir à La Tremblade ?',
        a: "Nous intervenons généralement sous 48 à 72h en basse saison (octobre à mai). En haute saison (juin à septembre), notre planning est plus chargé : comptez 5 à 8 jours d'attente. Pour une urgence avant l'arrivée de locataires saisonniers, nous faisons toujours notre maximum — appelez-nous au 06 25 68 48 49.",
      },
      {
        q: 'Nettoyez-vous les baies vitrées et vérandas en plus des fenêtres ?',
        a: "Oui, c'est notre spécialité. Nous traitons les fenêtres classiques, les baies vitrées coulissantes, les vérandas (vitrage + toiture polycarbonate), les fenêtres de toit (type Velux), les portes-fenêtres et même les garde-corps en verre. Tout est inclus dans un forfait global, pas de surprise sur la facture.",
      },
      {
        q: "Le crédit d'impôt 50 % est-il automatique à La Tremblade ?",
        a: "Oui, dès lors que la prestation a lieu à votre domicile (résidence principale ou secondaire à La Tremblade). Vous payez la facture intégrale et récupérez 50 % via votre déclaration d'impôts (case 7DB) — ou en avance immédiate Urssaf via notre partenaire Unipros, vous ne payez alors que 50 % directement. Accessible même si vous n'êtes pas imposable.",
      },
      {
        q: 'Travaillez-vous aussi pour les locations saisonnières et les conciergeries ?',
        a: "Absolument. Nous accompagnons plusieurs conciergeries et propriétaires de meublés de tourisme à La Tremblade et Ronce-les-Bains. Tarifs préférentiels au forfait pour les interventions régulières (entrée/sortie de locataires). Contrats sans engagement, planning souple.",
      },
      {
        q: 'Quel matériel utilisez-vous pour les vitres en hauteur ?',
        a: "Pour les baies vitrées, vérandas et étages, nous utilisons une perche télescopique en carbone qui monte jusqu'à 14 mètres, alimentée par un système à eau pure (osmosée + déionisée). Aucun produit chimique nécessaire : l'eau pure attire la saleté et sèche sans laisser de trace. Plus sûr, plus écologique, et un résultat impeccable.",
      },
      {
        q: 'Que se passe-t-il si je ne suis pas satisfait ?',
        a: "Nous repassons gratuitement sous 48h. Notre note de 4,8/5 sur Google (28 avis vérifiés) atteste de notre engagement qualité. Aucun client mécontent à ce jour n'a quitté un chantier sans solution.",
      },
    ],
    relatedLinks: [
      { to: '/blog/nettoyage-vitres-royan-prix-delais-zones-intervention', label: 'Nettoyage de vitres à Royan : prix, délais et zones' },
      { to: '/blog/nettoyage-canape-la-tremblade-taches-odeurs', label: 'Nettoyage de canapé à La Tremblade : taches et odeurs' },
      { to: '/credit-impot-50-particuliers', label: "Crédit d'impôt 50 % — comment ça marche" },
      { to: '/#services', label: 'Tous nos services de nettoyage' },
    ],
  },
};
